import { checkIsInternal } from '@src/utility/createStorage';
import HttpService from '@src/utility/httpService';
import { config } from '@uhg-abyss/web/tools/config';
import { getJobId, startJobPolling } from '@src/utility/CommonBulkPoll';
import {
    PCMS_AMDSM_API_URL,
    PCMS_AMDSM_CORE_API_URL,
    PCMS_SOT_API_URL,
    PCMS_SOT_CORE_API_URL,
    PCMS_DETAILS_API_URL,
    PCMS_DETAILS_CORE_API_URL,
    PCMS_CARE_ASSIST_API_URL,
    PCMS_CARE_ASSIST_TIN_ACTIVATIONS_DETAILS_API_URL,
    PCMS_FILTER_API_URL,
    PCMS_FILTER_BULK_API_URL,
} from './apiUrlConstants';

const _coreHttpService = new HttpService();
const _bulkHttpService = new HttpService();

const getCoreApiBaseUrl = (): string => {
    if (checkIsInternal()) {
        return config('API_DOMAIN_INTERNAL') || '';
    }
    return config('API_DOMAIN_EXTERNAL') || '';
};

const getBulkApiBaseUrl = (): string => {
    return config('BULK_PROCESSING_API') || '';
};

_coreHttpService.setBaseURL(getCoreApiBaseUrl());
_bulkHttpService.setBaseURL(getBulkApiBaseUrl());

/** Returns true when GeoCriteria is set in session (Geo Segmentation flow). */
const isGeoSegmentationFlow = (): boolean => {
    const geoCriteria = sessionStorage.getItem('GeoCriteria');
    return geoCriteria !== 'null' && geoCriteria !== null;
};

/** Returns true when the PCMS Contract ID aggregation flow is active. */
const isPcmsContractIdFlow = (): boolean => {
    return sessionStorage.getItem('contractIdNavigation') === 'true';
};

/** Returns true when PCMS requests must use the bulk processing APIs. */
const isPcmsBulkFlow = (): boolean => {
    return isGeoSegmentationFlow() || (
        isPcmsContractIdFlow() && sessionStorage.getItem('hiVolumeContractFlow') === 'true'
    );
};

const BULK_JOB_POLL_INTERVAL_MS = 4000;

type BulkSubmitResponse = {
    Data?: {
        JobId?: string;
    };
};

type BulkFinalResponse = {
    Status?: string;
    StatusCode?: number;
    Message?: string;
    Data?: any;
    TimeStamp?: number;
};

const pollPcmsBulkProcess = async (
    endpoint: string,
    payload: any
): Promise<BulkFinalResponse | null> => {
    try {
        const submitResponse = await getJobId(endpoint, payload, config('BULK_PROCESSING_API') || '') as BulkSubmitResponse;
        const jobId = submitResponse?.Data?.JobId;

        if (!jobId) {
            return null;
        }

        return await new Promise<BulkFinalResponse>((resolve, reject) => {
            startJobPolling(
                jobId,
                {
                    onCompleted: (result) => {
                        // result = pollingData.Data.Result (the actual data array) when Status === 'COMPLETED'
                        resolve({
                            Status: 'OK',
                            StatusCode: 200,
                            Message: 'Successfully Data Retrieved',
                            Data: result,
                        });
                    },
                    onFailed: (errorMessage) => {
                        reject(new Error(errorMessage));
                    },
                },
                '/job-status/result',
                BULK_JOB_POLL_INTERVAL_MS,
                undefined,
                false  // useMessageCheck: false — check Data.Status === 'COMPLETED', not top-level Message
            );
        });
    } catch {
        return null;
    }
};

export const getAMDSMData= async (payload:any) =>{
    if (isPcmsBulkFlow()) {
        return pollPcmsBulkProcess(PCMS_AMDSM_API_URL, payload);
    }
    try {
        const responseData: any = await _coreHttpService.push(PCMS_AMDSM_CORE_API_URL, payload);
        return responseData;
    } catch {
        return null;
    }
}

export const getSOTData= async (payload:any) =>{
    if (isPcmsBulkFlow()) {
        return pollPcmsBulkProcess(PCMS_SOT_API_URL, payload);
    }
    try {
        const responseData: any = await _coreHttpService.push(PCMS_SOT_CORE_API_URL, payload);
        return responseData;
    } catch {
        return null;
    }
}

export const getCareAssistData = async (payload:any) =>{
    const url = PCMS_CARE_ASSIST_API_URL;
    try {
        const responseData: any = await _coreHttpService.push(url, payload);
        return responseData;
    } catch(error) {
        throw error;
    }
}

export const getCareAssistTinActivationsDetailsData = async (payload:any) =>{
    const url = PCMS_CARE_ASSIST_TIN_ACTIVATIONS_DETAILS_API_URL;
    try {
        const responseData: any = await _coreHttpService.push(url, payload);
        return responseData;
    } catch(error) {
        throw error;
    }
}
export const getPcmsMetricDetails= async (payload:any, requestType: string) =>{
    if (isPcmsBulkFlow()) {
        return pollPcmsBulkProcess(PCMS_DETAILS_API_URL + requestType, payload);
    }
    try {
        const responseData: any = await _coreHttpService.push(PCMS_DETAILS_CORE_API_URL + requestType, payload);
        return responseData;
    } catch {
        return null;
    }
}

export const getPcmsFilterData = async (payload: any): Promise<any> => {
    try {
        // Date options for both Geo Segmentation and Contract ID flows are served by the bulk topfilter endpoint.
        const isGeoFlow = isGeoSegmentationFlow() || isPcmsContractIdFlow();
        const endpoint = isGeoFlow ? PCMS_FILTER_BULK_API_URL : PCMS_FILTER_API_URL;
        const httpService = isGeoFlow ? _bulkHttpService : _coreHttpService;
        const responseData: any = await httpService.push(endpoint, payload);
        return responseData;
    } catch {
        return null;
    }
};
